import { failureFrom, UNREACHABLE, type ApiResult } from "./api-result";
import type { Api, ResultOf, Route } from "./api-types";

/**
 * The one way this console talks to the api.
 *
 * Every request goes to the console's OWN origin at `/api/*` and is forwarded by
 * `server/main.ts`, which holds the session bearer in an httpOnly cookie and
 * `INTERNAL_API_SECRET` in its process. So there is no token in this module, no
 * `Authorization` header and no api base URL: the browser cannot leak what it
 * never had.
 */

const REQUESTED_BY = { "x-requested-by": "shoppy-admin" } as const;

type PathOf<E> = E extends { path: infer P } ? P : never;
type QueryOf<E> = E extends { query: infer Q } ? Q : never;
type BodyOf<E> = E extends { body: infer B } ? B : never;

export type CallOptions<K extends Route> = (Api[K] extends { path: unknown }
  ? { path: PathOf<Api[K]> }
  : { path?: never }) &
  (Api[K] extends { query: unknown } ? { query?: QueryOf<Api[K]> } : { query?: never }) &
  (Api[K] extends { body: unknown } ? { body: BodyOf<Api[K]> } : { body?: never }) & {
    signal?: AbortSignal;
  };

/**
 * `call("GET /v1/admin/merchants", { query: { status: "in_review" } })`.
 *
 * Never throws for a refusal. A 403 with «this payout is held pending KYB
 * re-verification» is the answer the operator came for, and an exception would
 * make every call site decide again whether to show it.
 */
export async function call<K extends Route>(
  route: K,
  ...args: CallOptions<K> extends { path?: never; query?: never; body?: never }
    ? [options?: CallOptions<K>]
    : [options: CallOptions<K>]
): Promise<ApiResult<ResultOf<K>>> {
  const options = (args[0] ?? {}) as {
    path?: Record<string, string>;
    query?: Record<string, unknown>;
    body?: unknown;
    signal?: AbortSignal;
  };
  const [method, template] = splitRoute(route);
  const url = `/api${fillPath(template, options.path)}${queryString(options.query)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers:
        options.body === undefined
          ? { accept: "application/json", ...REQUESTED_BY }
          : { accept: "application/json", "content-type": "application/json", ...REQUESTED_BY },
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
      // The bearer lives in an httpOnly cookie on this origin.
      credentials: "same-origin",
    });
  } catch (error) {
    // An aborted request is a screen that moved on, not a failure to report.
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return UNREACHABLE;
  }

  if (response.status === 204) return { ok: true, data: null as ResultOf<K> };

  const body = await readBody(response);
  if (!response.ok) return failureFrom(response.status, body);
  return { ok: true, data: body as ResultOf<K> };
}

/**
 * The one multipart call the console makes: a bank's settlement sheet
 * (`POST /v1/finance/rail-files`). It is outside the typed table because its
 * body is a file rather than a DTO, and giving the table a `FormData` arm would
 * weaken every other entry's `body` type to keep one of them honest.
 */
export async function upload(
  path: string,
  query: Record<string, unknown>,
  form: FormData,
  signal?: AbortSignal,
): Promise<ApiResult<unknown>> {
  let response: Response;
  try {
    response = await fetch(`/api${path}${queryString(query)}`, {
      method: "POST",
      // No `content-type`: the browser sets it with the multipart boundary.
      headers: { accept: "application/json", ...REQUESTED_BY },
      body: form,
      signal,
      credentials: "same-origin",
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return UNREACHABLE;
  }
  const body = await readBody(response);
  return response.ok ? { ok: true, data: body } : failureFrom(response.status, body);
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // An HTML error page from something in front of the api is not a problem
    // document; keeping the text means the honest failure block can show it.
    return { detail: text.slice(0, 500) };
  }
}

export function splitRoute(route: string): [method: string, template: string] {
  const at = route.indexOf(" ");
  return [route.slice(0, at), route.slice(at + 1)];
}

export function fillPath(template: string, path: Record<string, string> | undefined): string {
  return template.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_, name: string) => {
    const value = path?.[name];
    if (value === undefined || value === null || value === "") {
      throw new Error(`api: the route needs a "${name}" path parameter`);
    }
    // Every id here is a uuid or a slug, but encoding is not conditional on that:
    // an unencoded `/` in a parameter would change which route the front door
    // matched, and the front door refuses a parameter containing one.
    return encodeURIComponent(value);
  });
}

export function queryString(query: Record<string, unknown> | undefined): string {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // `undefined` and `null` mean "do not narrow", not "narrow to nothing" —
    // sending `status=` would make an unfiltered register look like an empty one.
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const text = params.toString();
  return text === "" ? "" : `?${text}`;
}
